import React, { useState, useEffect } from 'react';
import { adminAPI } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Users, Shield, Folder, Building, Video, Hexagon, ArrowRight } from 'lucide-react';

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/projects');
      return;
    }
    loadStats();
  }, [user, navigate]);

  const loadStats = async () => {
    try {
      setLoading(true);
      const result = await adminAPI.getStats();
      setStats(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96 text-muted-foreground">Loading admin dashboard...</div>;
  }

  if (error) {
    return <div className="p-4 border border-destructive bg-destructive/10 text-destructive rounded-lg">{error}</div>;
  }

  const statCards = [
    { icon: Users, label: 'Total Users', value: stats?.stats?.totalUsers || 0, color: 'from-blue-500 to-blue-600' },
    { icon: Shield, label: 'Admins', value: stats?.stats?.totalAdmins || 0, color: 'from-red-500 to-red-600' },
    { icon: Folder, label: 'Projects', value: stats?.stats?.totalProjects || 0, color: 'from-purple-500 to-purple-600' },
    { icon: Building, label: 'Sites', value: stats?.stats?.totalSites || 0, color: 'from-orange-500 to-orange-600' },
    { icon: Video, label: 'Cameras', value: stats?.stats?.totalCameras || 0, color: 'from-green-500 to-green-600' },
    { icon: Hexagon, label: 'Zones', value: stats?.stats?.totalZones || 0, color: 'from-cyan-500 to-cyan-600' },
  ];

  return (
    <div className="space-y-8 px-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Admin Dashboard</h1>
          <p className="text-muted-foreground mt-1">Manage your platform and monitor system metrics</p>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {statCards.map((stat, index) => (
          <Card key={index} className="overflow-hidden">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{stat.label}</CardTitle>
              <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${stat.color} flex items-center justify-center text-white shadow-lg`}>
                <stat.icon className="h-5 w-5" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stat.value}</div>
              <p className="text-xs text-muted-foreground mt-1">
                {stat.label === 'Total Users' && 'Registered accounts'}
                {stat.label === 'Admins' && 'System administrators'}
                {stat.label === 'Projects' && 'Active projects'}
                {stat.label === 'Sites' && 'Configured sites'}
                {stat.label === 'Cameras' && 'Camera feeds'}
                {stat.label === 'Zones' && 'Configured zones'}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Recent Users</CardTitle>
          <CardDescription>Latest registered users on the platform</CardDescription>
        </CardHeader>
        <CardContent>
          {stats?.recentUsers?.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No users yet</p>
          ) : (
            <div className="space-y-3">
              {stats?.recentUsers?.map((userData) => (
                <div key={userData.id} className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 transition-colors">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center text-primary-foreground font-semibold">
                      {userData.name?.charAt(0) || userData.email.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium">{userData.name || 'No name'}</p>
                      <p className="text-sm text-muted-foreground">{userData.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Badge variant={userData.role === 'admin' ? 'destructive' : 'secondary'} className="uppercase">
                      {userData.role}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {new Date(userData.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="mt-6 pt-4 border-t">
            <Button onClick={() => navigate('/admin/users')} className="w-full">
              Manage All Users
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
